// color ideas!
// https://www.heavy.ai/blog/12-color-palettes-for-telling-better-stories-with-your-data
// https://carbondesignsystem.com/data-visualization/color-palettes/

import {reactive} from "vue";
import countryCodeLookup from "country-code-lookup";
import * as openalexId from "@/openalexId";
import {getEntityIdentity, onCatalogUpdated} from "@/metaCatalog";
import {canonicalRecordId} from "@/ssr/recordRoutes";

// Entity identity labels (displayName, displayNameSingular) derive from the
// server /meta catalog — see the merge below the literal (oxjob #424). An
// authored value in an entry is a deliberate client override and always wins.
// Reactive so the post-boot live-catalog refresh propagates into rendered
// components.
const entityConfigs = reactive({
    works: {
        icon: "mdi-file-document-outline",
        name: "works",
        entityType: "works",
        nameSingular: "work",
        exportMode: "async",
        category: "research-output",
        descr: "Scholarly papers, books, datasets, etc.",
        placeholder: "Search scholarly papers, books, and more",
        filterKey: "ids.openalex",
        hintVerb: "by",
        color: "blue",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: true,
        highlightFilters: [
            {key: "open_access.is_oa", value: true, displayName: "Open Access works"},
            {key: "authorships.institutions.is_global_south", value: true, displayName: "from the Global South"},
            {key: "type", value: "dataset", displayName: "datasets"},
        ],
        rowsToShowOnEntityPage: [
            "publication_year",
            "type",
            "study_designs.id",
            "abstract",
            "primary_location.source.id",
            "doi",
            "authorships.author.id",
            "authorships.institutions.lineage",
            "language",
            null,
            "primary_topic.id",
            "primary_topic.subfield.id",
            "primary_topic.field.id",
            "primary_topic.domain.id",
            "sustainable_development_goals.id",
            null,
            "open_access.oa_status",
            "apc_paid.value_usd",
            null,
            "funders.id",
            "awards.id",
        ],
        metricsToShowOnEntityPage: [
            // "cited_by_count" intentionally omitted — the works facet at facetConfigs.js
            // has no extractFn, and "cited_by" below renders the same number with the
            // filter:cites:W… click-through users actually want (zd#8767).
            "fwci",
            "cites",
            "cited_by",
            "related_to",
        ],
        groupByDefaults: [
            "publication_year",
            "open_access.is_oa",
            "primary_topic.id",
            "authorships.institutions.lineage",
            "type",
        ],
        // Default table-view columns (ordered). The mandatory `display_name`
        // identity column leads. Other entity types have no `defaultColumns`
        // yet; useColumnsState falls back to ["display_name"] (which renders as
        // a link to the row). Phase 6's catalog sweep adds per-property column
        // blocks and richer per-entity defaults.
        defaultColumns: [
            "display_name",
            "authorships.author.id",
            "publication_year",
            "cited_by_count",
            "open_access.is_oa",
        ],
    },
    awards: {
        icon: "mdi-cash-multiple",
        name: "awards",
        entityType: "awards",
        nameSingular: "award",
        // API rows carry `title`, not `display_name` — api.js copies it over
        // at the boundary (config-driven; oxjob #852).
        displayNameField: "title",
        exportMode: "async",
        category: "funding",
        descr: "Grants funding scholarly research",
        placeholder: "Search grant awards",
        filterKey: "awards.id",
        hintVerb: "funded by",
        color: "brown",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: true, //  Temporarily disabled - awards entity is not ready for production yet.
        rowsToShowOnEntityPage: [
            "description",
            null,
            "funder.id",
            "amount",
            "funding_type",
            "funder_scheme",
            null,
            "start_date",
            "end_date",
            null,
            "investigators",
            "investigators.affiliation",
            null,
            "primary_topic.id",
            "primary_topic.subfield.id",
            "primary_topic.field.id",
            "primary_topic.domain.id",
            null,
            "funded_outputs_count",
            "funder_award_id",
        ],
        // Awards have no works_count or cited_by_count, so the default metrics
        // would render nothing and log two "no facet found" lines per page
        // (on the server since #1689's SSR).
        metricsToShowOnEntityPage: [],
        groupByDefaults: [
            "start_year",
            "funder.id",
            "primary_topic.id",
            "funding_type",
        ],
    },
    authors: {
        // icon: "mdi-account-school-outline",
        icon: "mdi-account-outline",
        name: "authors",
        entityType: "authors",
        nameSingular: "author",
        exportMode: "async",
        category: "people-orgs",
        descr: "Creators of scholarly works",
        placeholder: "Search scholarly authors",
        filterKey: "authorships.author.id",
        hintVerb: "at",
        color: "green",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: true,
        highlightFilters: [
            {key: "has_orcid", value: true, displayName: "with ORCIDs"},
            {key: "last_known_institutions.is_global_south", value: true, displayName: "from the Global South"},
        ],
        rowsToShowOnEntityPage: [
            "display_name_alternatives",
            "last_known_institutions.id",
            "affiliations.institution.id",
            "ids.orcid",
            "observed_orcids",
        ],
        metricsToShowOnEntityPage: [
            "works_count",
            "cited_by_count",
            "summary_stats.h_index",
            "summary_stats.i10_index",
        ],
        groupByDefaults: [
            "last_known_institutions.id",
            "last_known_institutions.country_code",
            "has_orcid",
        ],
        // Default table-view columns + the list-view export seed (job #304).
        defaultColumns: [
            "display_name",
            "last_known_institutions.id",
            "works_count",
            "cited_by_count",
            "summary_stats.h_index",
        ],
    },
    sources: {
        // icon: "mdi-book-outline",
        icon: "mdi-book-open-outline",
        name: "sources",
        entityType: "sources",
        nameSingular: "source",
        exportMode: "async",
        category: "publishing",
        descr: "Journals, conferences, and repositories",
        placeholder: "Search academic journals & repositories",
        filterKey: "primary_location.source.id",
        hintVerb: "published by",
        color: "orange",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: true,
        highlightFilters: [
            {key: "is_oa", value: true, displayName: "that are Open Access"},
        ],
        rowsToShowOnEntityPage: [
            "homepage_url",
            "issn",
            "type",
            "publisher",
            "alternate_titles",
            null,
            "is_oa",
            "is_in_doaj",
            "apc_usd",
        ],
        metricsToShowOnEntityPage: [
            "works_count",
            "cited_by_count",
            "summary_stats.h_index",
            "summary_stats.i10_index",
            "summary_stats.2yr_mean_citedness",
        ],
        groupByDefaults: [
            "type",
            "topics.id",
            "is_oa",
            "is_in_doaj",
            "listed_in",
        ],
        defaultColumns: [
            "display_name",
            "type",
            "publisher",
            "works_count",
            "cited_by_count",
        ],
    },
    publishers: {
        // icon: "mdi-book-outline",
        icon: "mdi-domain",
        name: "publishers",
        entityType: "publishers",
        nameSingular: "publisher",
        exportMode: "async",
        category: "publishing",
        descr: "Organizations that publish scholarly journals and books",
        placeholder: "Search academic publishers",
        filterKey: "primary_location.source.host_organization_lineage",
        color: "pink",
        hasAutocomplete: true,
        isNative: true,
        rowsToShowOnEntityPage: [
            "homepage_url",
            "alternate_titles",
            "parent_publisher",
            "country_codes",
        ],
        defaultColumns: [
            "display_name",
            "country_codes",
            "works_count",
            "cited_by_count",
        ],
    },
    funders: {
        icon: "mdi-cash-multiple",
        name: "funders",
        entityType: "funders",
        nameSingular: "funder",
        exportMode: "async",
        category: "funding",
        descr: "Organization that funds research",
        placeholder: "Search research funders",
        filterKey: "funders.id",
        hintVerb: "funded by",
        color: "brown",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: true,
        rowsToShowOnEntityPage: [
            "homepage_url",
            "alternate_titles",
            "description",
            null,
            "country_code",
            "ids.ror",
            "ids.wikidata",
            "ids.crossref",
            "ids.doi",
        ],
        metricsToShowOnEntityPage: [
            "works_count",
            "cited_by_count",
            "summary_stats.h_index",
            "summary_stats.i10_index",
            "summary_stats.2yr_mean_citedness",
            "awards_count",
        ],
        groupByDefaults: [
            "country_code",
            "is_global_south",
        ],
        defaultColumns: [
            "display_name",
            "country_code",
            "works_count",
            "cited_by_count",
        ],
    },
    institutions: {
        icon: "mdi-town-hall",
        name: "institutions",
        entityType: "institutions",
        nameSingular: "institution",
        exportMode: "async",
        category: "people-orgs",
        descr: "Universities and research centers",
        placeholder: "Search academic institutions",
        filterKey: "authorships.institutions.lineage",
        hintVerb: "in",
        color: "purple",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: true,
        rowsToShowOnEntityPage: [
            "homepage_url",
            "display_name_alternatives",
            "parent_institutions",
            "child_institutions",
            "related_institutions",
            "ids.ror",
        ],
        // Works counted directly, with child institutions, and also with the
        // institutions it replaced (oxjob #1823); a row equal to the one above hides.
        metricsToShowOnEntityPage: [
            "works_count",
            "works_count_with_children",
            "works_count_with_children_and_predecessors",
            "cited_by_count",
        ],
        groupByDefaults: [
            "country_code",
            "type",
        ],
        defaultColumns: [
            "display_name",
            "country_code",
            "type",
            "works_count",
            "cited_by_count",
        ],
    },
    concepts: {
        icon: "mdi-tag-outline",
        name: "concepts",
        entityType: "concepts",
        nameSingular: "concept",
        category: "topics",
        descr: "Topics and fields of study",
        placeholder: "Search topics",
        filterKey: "concepts.id",
        hasAutocomplete: true,
        isNative: true,
        rowsToShowOnEntityPage: [],
        defaultColumns: [
            "display_name",
            "level",
            "works_count",
            "cited_by_count",
        ],
    },
    keywords: {
        icon: "mdi-tag-outline",
        name: "keywords",
        entityType: "keywords",
        nameSingular: "keyword",
        exportMode: "async",
        category: "topics",
        descr: "Author-assigned and extracted terms describing research content",
        placeholder: "Search keywords",
        filterKey: "keywords.id",
        hintVerb: ":",
        hasAutocomplete: true,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            null,
            "primary_topic.id",
            "topics.id",
            "ids.wikidata",
        ],
    },
    topics: {
        icon: "mdi-tag-outline",
        name: "topics",
        entityType: "topics",
        nameSingular: "topic",
        exportMode: "async",
        category: "topics",
        descr: "Algorithmically clustered research areas (~4,500 entries)",
        placeholder: "Search topics",
        // hasSerp: true,
        filterKey: "primary_topic.id",
        hintVerb: ":",
        hasAutocomplete: true,
        isNative: true,
        rowsToShowOnEntityPage: [
            "description",
            null,
            "keywords",
            "siblings",
            "subfield.id",
            "field.id",
            "domain.id",
        ],
        groupByDefaults: [
            "subfield.id",
            "field.id",
            "domain.id",
        ],
        defaultColumns: [
            "display_name",
            "subfield.id",
            "works_count",
            "cited_by_count",
        ],
    },
    subfields: {
        icon: "mdi-tag-outline",
        name: "subfields",
        entityType: "subfields",
        nameSingular: "subfield",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "field.display_name", label: "Field" },
            { key: "domain.display_name", label: "Domain" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "topics",
        descr: "Mid-level academic disciplines (~250 entries) grouping related topics",
        placeholder: "Search subfields",
        filterKey: "primary_topic.subfield.id",
        hasAutocomplete: true,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "display_name_alternatives",
            null,
            "topics",
            "siblings",
            "field.id",
            "domain.id",
        ],
        groupByDefaults: [
            "field.id",
            "domain.id",
        ],
    },
    fields: {
        icon: "mdi-tag-outline",
        name: "fields",
        entityType: "fields",
        nameSingular: "field",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "domain.display_name", label: "Domain" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "topics",
        descr: "Broad academic disciplines (~25) like Biology, Chemistry, Physics",
        placeholder: "Search fields",
        filterKey: "primary_topic.field.id",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "display_name_alternatives",
            null,
            "subfields",
            "siblings",
            "domain.id",
        ],
        groupByDefaults: [
            "domain.id",
        ],
    },
    domains: {
        icon: "mdi-tag-outline",
        name: "domains",
        entityType: "domains",
        nameSingular: "domain",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "topics",
        descr: "Four top-level research areas: Life Sciences, Physical Sciences, Health Sciences, Social Sciences",
        placeholder: "Search domains",
        filterKey: "primary_topic.domain.id",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "display_name_alternatives",
            null,
            "fields",
            "siblings",
        ],
    },
    sdgs: {
        icon: "mdi-sprout-outline",
        name: "sdgs",
        entityType: "sdgs",
        nameSingular: "sdg",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "topics",
        descr: "United Nations Sustainable Development Goals linked to research",
        placeholder: "Search SDGs",
        filterKey: "sustainable_development_goals.id",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    countries: {
        icon: "mdi-earth",
        name: "countries",
        entityType: "countries",
        nameSingular: "country",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "continent.display_name", label: "Continent" },
            { key: "is_global_south", label: "Global South" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "geo-language",
        descr: "Countries where research-affiliated institutions are located",
        placeholder: "Search countries",
        filterKey: "authorships.countries",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "display_name_alternatives",
            null,
            "continent",
            "is_global_south",
        ],
    },
    continents: {
        icon: "mdi-earth",
        name: "continents",
        entityType: "continents",
        nameSingular: "continent",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
        ],
        category: "geo-language",
        descr: "Continental regions for geographic analysis of research",
        placeholder: "Search continents",
        filterKey: "authorships.institutions.continent",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "countries",
        ],
    },
    languages: {
        icon: "mdi-translate",
        name: "languages",
        entityType: "languages",
        nameSingular: "language",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "geo-language",
        descr: "Languages in which scholarly works are written",
        placeholder: "Search languages",
        filterKey: "language",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [],
    },
    types: {
        icon: "mdi-shape-outline",
        name: "types",
        entityType: "types",
        nameSingular: "type",
        // Override: the server entity is "work-types" ("work types"/"work
        // type"); the GUI deliberately uses the shorter label.
        displayName: "types",
        displayNameSingular: "type",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "research-output",
        descr: "Formats of scholarly output: articles, books, datasets, preprints, etc.",
        placeholder: "Search work types",
        filterKey: "type",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "crossref_types",
        ],
    },
    "source-types": {
        icon: "mdi-shape-outline",
        name: "source-types",
        entityType: "source-types",
        nameSingular: "source type",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "publishing",
        descr: "Categories of publication venues: journals, repositories, conferences",
        placeholder: "Search source types",
        filterKey: "primary_location.source.type",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    "institution-types": {
        icon: "mdi-shape-outline",
        name: "institution-types",
        entityType: "institution-types",
        nameSingular: "institution type",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "people-orgs",
        descr: "Categories of organizations: universities, companies, hospitals, governments",
        placeholder: "Search institution types",
        filterKey: "authorships.institutions.type",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    "licenses": {
        icon: "mdi-lock-open-outline",
        name: "licenses",
        entityType: "licenses",
        nameSingular: "license",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "open-access",
        descr: "Open Access licenses like CC-BY, CC-BY-SA, and others",
        placeholder: "Search licenses",
        filterKey: "best_oa_location.license",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    "oa-statuses": {
        icon: "mdi-lock-open-outline",
        name: "oa-statuses",
        entityType: "oa-statuses",
        nameSingular: "oa status",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "open-access",
        descr: "Open Access classifications: gold, green, hybrid, bronze, closed",
        placeholder: "Search OA statuses",
        filterKey: "open_access.oa_status",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    "indexes": {
        icon: "mdi-tag-outline",
        name: "indexes",
        entityType: "indexes",
        nameSingular: "index",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "publishing",
        descr: "Bibliographic indexes that list works: Crossref, PubMed, DataCite, DOAJ, arXiv",
        placeholder: "Search indexes",
        filterKey: "indexed_in",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    // oxjob #1205: the vocabulary behind sources.listed_in. Non-normative: a list
    // page says who maintains it and what it covers, never that we endorse it.
    "source-lists": {
        icon: "mdi-format-list-bulleted",
        name: "source-lists",
        entityType: "source-lists",
        nameSingular: "source list",
        // authored identity: metaSnapshot.json predates the entity (regen pulls
        // in a month of unrelated drift); the live /meta catalog agrees.
        displayName: "source lists",
        displayNameSingular: "source list",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "maintainer", label: "Maintainer" },
            { key: "url", label: "URL" },
            { key: "list_version", label: "Edition" },
            { key: "sources_count", label: "Sources Count" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "publishing",
        descr: "External journal lists a source can appear on: DOAJ, CWTS Core, national lists",
        placeholder: "Search source lists",
        filterKey: "primary_location.source.listed_in",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "maintainer",
            "url",
            "list_version",
            "sources_count",
        ],
    },
    // oxjob #1312: how the research inside a work was done (closed vocabulary of
    // seven PubMed-backed designs). `type` says what kind of document a work is;
    // an editorial has no study design.
    "study-designs": {
        icon: "mdi-flask-outline",
        name: "study-designs",
        entityType: "study-designs",
        nameSingular: "study design",
        // authored identity, as source-lists: the live /meta catalog agrees
        displayName: "study designs",
        displayNameSingular: "study design",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "topics",
        descr: "How the research was done: randomized controlled trial, systematic review, case report and more",
        placeholder: "Search study designs",
        filterKey: "study_designs.id",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "pubmed_publication_types",
        ],
    },
    "locations": {
        icon: "mdi-file-document-outline",
        name: "locations",
        entityType: "locations",
        nameSingular: "location",
        // Component entity (oxjob #852): a work is a roll-up of locations
        // (1:many). Mirrors openalexId.COMPONENT_ENTITY_TYPES — opaque,
        // case-significant, slash-bearing short ids (doi:…, pmh:/…, mag:…).
        entityClass: "component",
        // API rows carry `title`, not `display_name` — api.js copies it over
        // at the boundary (displayNameField generalizes the old awards hack).
        displayNameField: "title",
        // Kept authored: /meta/entities doesn't serve the locations
        // pseudo-entity.
        displayName: "locations",
        displayNameSingular: "work location",
        descr: "Individual copies of works, harvested from repositories and other sources",
        // The box's headline affordance is the record lookup (paste a DOI or
        // your repository's own record ID → jump to the location page);
        // full-text search rides #915-v3's analyzed title.
        placeholder: "Search locations or paste a record ID",
        filterKey: "locations.id",
        hasAutocomplete: false,
        isNative: false,
        // SHIPPED 2026-08-30 (oxjob #852): search live (elastic-api 8285f51,
        // analyzed title.text since locations-v3). Jason dropped the
        // delete-path gate — ghosts may re-accumulate until #915/v3's delete
        // path lands. This also auto-adds /locations to the router's
        // entityNames regex and the entity-type selector.
        hasSerp: true,
        category: "components",
        // No works_count/cited_by_count on /locations — an empty defaultSort
        // sends no sort param at all (API default order: work_id,native_id).
        // The generic non-works fallback ("works_count") 400s here (url.js).
        defaultSort: "",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "title", label: "Title" },
            { key: "work_id", label: "Work ID" },
            { key: "native_id", label: "Native ID" },
            { key: "native_id_namespace", label: "Native ID namespace" },
            { key: "source_name", label: "Source" },
            { key: "version", label: "Version" },
            { key: "license", label: "License" },
            { key: "is_oa", label: "Open Access" },
            { key: "provenance", label: "Provenance" },
            { key: "landing_page_url", label: "Landing page URL" },
            { key: "pdf_url", label: "PDF URL" },
        ],
        groupByDefaults: [
            "version",
            "license",
            "is_oa",
        ],
        defaultColumns: [
            "title",
            "source_id",
            "version",
            "license",
            "is_oa",
        ],
        rowsToShowOnEntityPage: [
            "work_id",
            "source_id",
            "landing_page_url",
            "pdf_url",
            null,
            "is_oa",
            "version",
            "license",
            "type",
            "language",
            null,
            "id",
            "native_id",
            "native_id_namespace",
            "provenance",
        ],
        metricsToShowOnEntityPage: [],
    },
})

// Derive entity identity labels from the /meta catalog (snapshot-seeded, then
// live-refreshed — see metaCatalog.js). Authored keys captured here first are
// client overrides and are never overwritten.
const derivedIdentityKeys = ["displayName", "displayNameSingular"];
const authoredIdentityKeys = new Map(
    Object.values(entityConfigs).map(c =>
        [c.name, new Set(derivedIdentityKeys.filter(k => k in c))]
    )
);
const applyEntityIdentity = () => {
    for (const c of Object.values(entityConfigs)) {
        const identity = getEntityIdentity(c.name);
        if (!identity) continue;
        for (const key of derivedIdentityKeys) {
            if (identity[key] && !authoredIdentityKeys.get(c.name).has(key)) {
                c[key] = identity[key];
            }
        }
    }
};
applyEntityIdentity();
onCatalogUpdated(applyEntityIdentity);


const metricsPriorityOrder = [
    "works_count",
    "works_count_with_children",
    "works_count_with_children_and_predecessors",
    "cited_by_count",
    "summary_stats.h_index",
    "summary_stats.i10_index",
    "summary_stats.2yr_mean_citedness",
    "awards_count",
    "fwci",
    "cites",
    "cited_by",
    "related_to",
]

const defaultMetricsToShowOnEntityPage = ["works_count", "cited_by_count"];
for (const c of Object.values(entityConfigs)) {
    if (c.metricsToShowOnEntityPage === undefined) {
        c.metricsToShowOnEntityPage = defaultMetricsToShowOnEntityPage;
    }
}

const entityConfigBySingular = Object.fromEntries(
    Object.values(entityConfigs).map(c => [c.nameSingular, c])
);


const entityCategories = [
    { id: 'research-output', name: 'Research output' },
    { id: 'people-orgs', name: 'People & organizations' },
    { id: 'publishing', name: 'Publishing' },
    { id: 'funding', name: 'Funding' },
    { id: 'topics', name: 'Topics & classification' },
    { id: 'geo-language', name: 'Geography & language' },
    { id: 'open-access', name: 'Open Access' },
    // Component entities (oxjob #852): sub-work entities a work rolls up.
    { id: 'components', name: 'Work components' },
];

const getEntitiesForBrowser = function () {
    return getEntityConfigs().filter(c =>
        c.name !== 'concepts'
    );
}

const getEntityConfigs = function () {
    return Object.values(entityConfigs);
}


const getEntityConfig = function (name) {
    if (!name) {
        console.trace();
        throw new Error("OpenAlex: getEntityConfig(): you must provide a @name argument.")
    }
    return entityConfigs[name] ?? entityConfigBySingular[name];
}


// Explicit mapping from OpenAlex ID prefix to native entity "name"
// e.g. W123 → "works", A123 → "authors", G123 → "awards"
const nativeIdPrefixToEntityType = {
    w: "works",
    a: "authors",
    i: "institutions",
    s: "sources",
    p: "publishers",
    f: "funders",
    c: "concepts",
    t: "topics",
    g: "awards", // G = awards
};

/**
 * @deprecated Use openalexId.getEntityType() instead
 */
const nativeEntityTypeFromId = function (id) {
    const parsed = openalexId.parseId(id);
    if (parsed && parsed.isNative) {
        return parsed.entityType;
    }
    return undefined;
}


/**
 * @deprecated Use openalexId.getEntityType() instead
 */
const externalEntityTypeFromId = function (id) {
    const parsed = openalexId.parseId(id);
    if (parsed && !parsed.isNative) {
        return parsed.entityType;
    }
    return undefined;
}

/**
 * @deprecated Use openalexId.parseId() instead
 */
const parseEntityId = function (id) {
    const parsed = openalexId.parseId(id);
    if (!parsed) {
        throw new Error(`OpenAlex: parseEntityId(): invalid id: ${id}`);
    }
    return {
        entityType: parsed.entityType,
        entityKey: parsed.shortId,
    };
}


/**
 * @deprecated Use openalexId.parseId() instead
 */
const urlPartsFromId = function (id) {
    const parsed = openalexId.parseId(id);
    if (!parsed) {
        return { entityType: undefined, entityId: undefined };
    }
    return {
        entityType: parsed.entityType,
        entityId: canonicalRecordId(parsed.entityType, parsed.shortId),  // #1689: canonical case, no 301
    };
}

// maybe make something that parses an id, no matter what the format, and returns the entityType and entityId

const getLocationString = function (entity) {
    if (!entity || !entity?.country_code) return
    const country = countryCodeLookup.byIso(entity?.country_code)
        ?.country
        ?.replace("United States", "USA")
        ?.replace("United Kingdom", "UK")

    const locArr = [
        entity?.geo?.city,
        entity?.geo?.region,
        country,
    ].filter(x => x)
    return locArr.join(", ")
}


export {
    entityConfigs,
    entityCategories,
    metricsPriorityOrder,
    getEntityConfig,
    getEntityConfigs,
    getEntitiesForBrowser,
    getLocationString,
    nativeEntityTypeFromId,
    externalEntityTypeFromId,
    urlPartsFromId,
    parseEntityId,
}
